/*###########################################################################
 // Favorite alerts island menu
 //#########################################################################*/

import { showSiteToast } from './_siteToast'
import { showConfirm } from './_confirmDialog'

const DISABLE_ICON = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18"/>
  </svg>
`

function config() {
  const el = document.getElementById('favorite-alerts-config')
  if (!el) return null
  try {
    return JSON.parse(el.textContent || '{}')
  } catch {
    return null
  }
}

function menu() {
  return document.querySelector('[data-favorite-alerts-menu]')
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return 'Alert'
  const num = Number(value)
  if (Number.isNaN(num)) return 'Alert'
  return `≤ £${num.toFixed(2)}`
}

function renderFavorites(favorites) {
  const root = menu()
  const cfg = config()
  if (!root || !cfg) return

  if (!favorites.length) {
    root.innerHTML = `
      <p class="nav-fav__empty">Star an alert to pin it here for quick access.</p>
      <a class="nav-island__menu-item" href="${escapeHtml(cfg.alertsUrl)}" role="menuitem">
        <span>Open Alerts</span>
      </a>
    `
    return
  }

  const items = favorites.map((item) => {
    const detail = [item.numberLabel, formatPrice(item.maxPrice)].filter(Boolean).join(' · ')
    const thumb = item.imageUrl
      ? `<img src="${escapeHtml(item.imageUrl)}" alt="" class="nav-fav__thumb" loading="lazy">`
      : `<span class="nav-fav__thumb nav-fav__thumb--empty" aria-hidden="true"></span>`
    const name = item.name || 'this card'

    return `
      <div class="nav-fav" data-favorite-id="${escapeHtml(item.id)}">
        <a href="${escapeHtml(item.cardUrl)}" class="nav-fav__main">
          ${thumb}
          <span class="nav-fav__meta">
            <span class="nav-fav__name">${escapeHtml(item.name)}</span>
            <span class="nav-fav__detail">${escapeHtml(detail)}</span>
          </span>
        </a>
        <div class="nav-fav__actions">
          <a class="btn btn--poke btn--small" href="${escapeHtml(item.listingsUrl)}">Listings</a>
          <button
            type="button"
            class="btn btn--outline btn--small nav-fav__unstar"
            data-unfavorite-alert="${escapeHtml(item.id)}"
            aria-label="Remove from favorites"
            title="Unfavorite"
          >
            <span aria-hidden="true">★</span>
          </button>
          <button
            type="button"
            class="btn btn--circle btn--circle-danger"
            data-disable-alert="${escapeHtml(item.id)}"
            data-disable-name="${escapeHtml(name)}"
            aria-label="Disable alert"
            title="Disable alert"
          >
            ${DISABLE_ICON}
          </button>
        </div>
      </div>
    `
  }).join('')

  root.innerHTML = `${items}
    <a class="nav-fav__all" href="${escapeHtml(cfg.alertsUrl)}" role="menuitem">View all alerts</a>
  `
}

async function postFavorite(userCardId, favorited) {
  const cfg = config()
  if (!cfg?.toggleUrl) throw new Error('Missing favorite alerts config')

  const body = new FormData()
  body.append(cfg.csrfName, cfg.csrfToken)
  body.append('userCardId', String(userCardId))
  body.append('favorited', favorited ? '1' : '0')

  const res = await fetch(cfg.toggleUrl, {
    method: 'POST',
    body,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  const data = await res.json()
  if (!data.success) {
    throw new Error(data.error || 'Could not update favorite.')
  }

  if (Array.isArray(data.favorites)) {
    renderFavorites(data.favorites)
  } else {
    await refreshFavoriteAlerts()
  }

  window.dispatchEvent(new CustomEvent('app:favorite-alerts-changed', {
    detail: { userCardId, favorited: !!data.favorited, favorites: data.favorites || [] },
  }))

  return data
}

async function disableAlert(userCardId) {
  const cfg = config()
  if (!cfg?.disableUrl) throw new Error('Missing disable alert config')

  const body = new FormData()
  body.append(cfg.csrfName, cfg.csrfToken)
  body.append('userCardId', String(userCardId))
  body.append('enabled', '0')

  const res = await fetch(cfg.disableUrl, {
    method: 'POST',
    body,
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  const data = await res.json()
  if (!data.success) {
    throw new Error(data.error || 'Could not disable alert.')
  }

  await refreshFavoriteAlerts()
  showSiteToast('Alert disabled.')
  window.dispatchEvent(new CustomEvent('app:alert-disabled', {
    detail: { userCardId },
  }))

  return data
}

export async function refreshFavoriteAlerts() {
  const cfg = config()
  if (!cfg?.listUrl || !menu()) return

  try {
    const res = await fetch(cfg.listUrl, {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    })
    const data = await res.json()
    if (Array.isArray(data.favorites)) {
      renderFavorites(data.favorites)
    }
  } catch {
    // keep SSR markup
  }
}

export async function setAlertFavorite(userCardId, favorited) {
  return postFavorite(userCardId, favorited)
}

export function initFavoriteAlerts() {
  if (document.documentElement.dataset.favoriteAlertsBound) return
  document.documentElement.dataset.favoriteAlertsBound = '1'

  document.addEventListener('click', async (e) => {
    const unfav = e.target.closest('[data-unfavorite-alert]')
    if (unfav) {
      e.preventDefault()
      e.stopPropagation()
      const id = Number(unfav.getAttribute('data-unfavorite-alert'))
      if (!id) return
      unfav.disabled = true
      try {
        await postFavorite(id, false)
        document.querySelectorAll(`[data-favorite-toggle="${id}"]`).forEach((el) => {
          el.setAttribute('aria-pressed', 'false')
          el.classList.remove('is-active')
          if (el.dataset.labelOff) el.textContent = el.dataset.labelOff
          if (el.hasAttribute('aria-label')) el.setAttribute('aria-label', 'Add to favorites')
          if (el.hasAttribute('title')) el.setAttribute('title', 'Favorite')
        })
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t update favorite.')
        unfav.disabled = false
      }
      return
    }

    const disableBtn = e.target.closest('[data-disable-alert]')
    if (disableBtn) {
      e.preventDefault()
      e.stopPropagation()
      const id = Number(disableBtn.getAttribute('data-disable-alert'))
      if (!id) return
      const name = disableBtn.getAttribute('data-disable-name') || 'this card'
      const ok = await showConfirm({
        title: 'Disable this alert?',
        message: `Stop watching Vinted for ${name}? You can re-enable the alert later from the card page.`,
        confirmLabel: 'Disable',
      })
      if (!ok) return
      disableBtn.disabled = true
      try {
        await disableAlert(id)
      } catch (err) {
        showSiteToast(err.message || 'Couldn’t disable alert.')
        disableBtn.disabled = false
      }
      return
    }

    const toggle = e.target.closest('[data-favorite-toggle]')
    if (!toggle) return

    e.preventDefault()
    const id = Number(toggle.getAttribute('data-favorite-toggle'))
    if (!id) return
    const next = toggle.getAttribute('aria-pressed') !== 'true'
    toggle.disabled = true
    try {
      await postFavorite(id, next)
      toggle.setAttribute('aria-pressed', next ? 'true' : 'false')
      toggle.classList.toggle('is-active', next)
      if (next && toggle.dataset.labelOn) toggle.textContent = toggle.dataset.labelOn
      if (!next && toggle.dataset.labelOff) toggle.textContent = toggle.dataset.labelOff
      if (toggle.hasAttribute('aria-label')) {
        toggle.setAttribute('aria-label', next ? 'Remove from favorites' : 'Add to favorites')
      }
      if (toggle.hasAttribute('title')) {
        toggle.setAttribute('title', next ? 'Unfavorite' : 'Favorite')
      }
    } catch (err) {
      showSiteToast(err.message || 'Couldn’t update favorite.')
    } finally {
      toggle.disabled = false
    }
  })

  // Lazy-load when the Alerts flyout opens (nav lives outside Swup).
  document.querySelectorAll('[data-alerts-flyout]').forEach((flyout) => {
    if (flyout.dataset.favRefreshBound) return
    flyout.dataset.favRefreshBound = '1'
    let loaded = false
    const load = () => {
      if (loaded) {
        refreshFavoriteAlerts()
        return
      }
      loaded = true
      refreshFavoriteAlerts()
    }
    flyout.addEventListener('mouseenter', load)
    flyout.querySelector('[data-flyout-trigger]')?.addEventListener('click', load)
  })
}
